import { FileText, Plus } from "lucide-react";
import { Link } from "@/lib/nav";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@buildpy/ui/table";
import MetaSep from "@/components/meta-sep";
import Money from "@/components/money";

import { dateAfterDays, formatDate, formatDateTime } from "@buildpy/core/lib/format";
import { contractVatModeOf, currentTotalOf } from "@buildpy/core/modules/change-order/calc";
import { statementPath } from "@buildpy/core/modules/change-order/current-statement";
import ChangeOrderList from "@/modules/change-order/ui/change-order-list";
import PaymentPaidToggle from "@/modules/change-order/ui/payment-paid-toggle";
import { canStartChangeOrder, newChangeOrderPath } from "@buildpy/core/modules/change-order/view";
import { printedAmount, usesDelayPenalty } from "@buildpy/core/modules/contract/calc";
import { bpToRate, RATE_FIELDS, RATE_UNIT_SUFFIX } from "@buildpy/core/modules/contract/settings";
import { CLAUSE_SOURCE_LABEL } from "@buildpy/core/modules/contract/types";
import ContractActions from "@/modules/contract/ui/contract-actions";
import ContractStatusBadge from "@/modules/contract/ui/status-badge";
import CreateProjectButton from "@/modules/project/ui/create-project-button";
import { contractSharePath, contractViewRecordText } from "@buildpy/core/modules/contract/view";
import { ORG_SETTING_DEFAULT } from "@buildpy/core/modules/org/types";
import { totalVatNote } from "@buildpy/core/modules/quote/snapshot-vat";
import type { ContractView } from "@buildpy/core/views/g2a/contract";
import { CrumbName } from "@/shell/crumb";
import PageHeader from "@/shell/page-header";

// SectionCard 안 촘촘한 표 — 공통 Table 기본(h-12·p-4)을 줄이고 왼쪽 여백을 없앤다.
const HEAD = "h-auto py-1 pl-0 pr-2 text-xs font-normal";
const CELL = "py-1.5 pl-0 pr-2";
// 받음 칸 — 폰의 체크박스 누름 영역(40px)이 표 밖으로 밀리지 않게 12px 비운다(Table 기본 :has(checkbox) pr-0 을 같은 변형으로 덮고, md 이상은 그대로).
const PAID_HEAD = "pr-3 text-right md:pr-1";
const PAID_CELL = "py-3 pl-0 text-right whitespace-nowrap md:py-1.5 [&:has([role=checkbox])]:pr-3 md:[&:has([role=checkbox])]:pr-0";

const last4Of = (phone: string): string => phone.replace(/\D/g, "").slice(-4);

// 화면에 보이는 요율 칸 — 편집 화면과 같은 세 칸(지체상금은 그 서식에서만).
const SHOWN_RATES = RATE_FIELDS.filter(
  (f) => f.key === "delay_rate_bp" || f.key === "delay_cap_bp" || f.key === "late_interest_rate_bp"
);

// 계약 상세. 금액은 printedAmount(언제나 총액)를 거치고, 체결본은 스냅샷 값을 쓴다.
const Page = ({ data }: { data: ContractView }) => {
  const { contract, payments, customer, snapshotAmount, live, viewRecord, validDaysSetting, project, changeOrders, origin } = data;

  const amount =
    snapshotAmount ??
    printedAmount(
      {
        total_amount: contract.total_amount,
        supply_amount: contract.supply_amount,
        vat_amount: contract.vat_amount,
      },
      contract.vat_included
    );

  const shareUrl = live ? `${origin}${contractSharePath(live.token)}` : null;
  const validDays = Number(validDaysSetting ?? ORG_SETTING_DEFAULT.quote_valid_days) || 30;
  const current = currentTotalOf(contract, changeOrders);
  const signed = contract.signed_at !== null;
  // 받음 표시는 체결·준공 계약만 — 저장소도 같은 조건으로 막는다.
  const paidEditable = contract.status === "SIGNED" || contract.status === "COMPLETED";
  const canChange = canStartChangeOrder(contract.status, project?.project.status);
  const rates = SHOWN_RATES.filter((f) => !f.delayOnly || usesDelayPenalty(contract.clause_source));

  return (
    <>
      <CrumbName name={contract.contract_no} />
      <PageHeader
        actions={
          <ContractActions
            contractId={contract.id}
            defaultExpiresDate={dateAfterDays(validDays)}
            defaultLast4={last4Of(contract.client_phone || customer?.phone || "")}
            shareUrl={shareUrl}
            status={contract.status}
          />
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-3 md:gap-x-2 md:gap-y-1">
            <ContractStatusBadge status={contract.status} />
            <span>{contract.title}</span>
            <MetaSep />
            {customer ? (
              <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/customers/${customer.id}`}>
                {customer.name}
              </Link>
            ) : (
              <span>{contract.client_name || "—"}</span>
            )}
            <MetaSep />
            <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/quotes/${contract.quote_id}`}>
              견적 보기
            </Link>
            {project && (
              <>
                <MetaSep />
                <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/projects/${project.project.id}`}>
                  현장 보기
                </Link>
              </>
            )}
          </span>
        }
        title={`계약 ${contract.contract_no}`}
      />

      {contract.status === "SIGNED" && !project && (
        <SectionCard
          className="flex flex-wrap items-center justify-between gap-2 text-sm"
          padding="sm"
          data-testid="contract-no-project">
          <span>체결된 계약인데 현장이 아직 없습니다. 현장을 만들면 착공일·공정표를 이어받습니다.</span>
          <CreateProjectButton contractId={contract.id} />
        </SectionCard>
      )}

      {live && shareUrl && (
        <SectionCard className="text-sm" padding="sm" data-testid="contract-share">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-muted-foreground">고객 링크</span>
            <code className="break-all">{contractSharePath(live.token)}</code>
            <span className="text-muted-foreground">만료</span>
            <span className="tabular-nums">{formatDateTime(live.expires_at)}</span>
          </div>
        </SectionCard>
      )}

      {/* 발송한 적이 있으면 체결 뒤에도 남긴다 — 직원 화면은 링크를 거치지 않아 수에 들어가지 않는다. */}
      {viewRecord && (
        <p className="text-sm tabular-nums text-muted-foreground" data-testid="contract-view-record">
          {contractViewRecordText(viewRecord, formatDateTime)}
        </p>
      )}

      {(contract.status === "VOID" || contract.status === "TERMINATED") && (
        <SectionCard className="text-sm" padding="sm">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-muted-foreground">{contract.status === "VOID" ? "파기일" : "해지일"}</span>
            <span className="tabular-nums">
              {formatDateTime(contract.status === "VOID" ? contract.voided_at : contract.terminated_at)}
            </span>
            <span className="text-muted-foreground">사유</span>
            <span>{(contract.status === "VOID" ? contract.void_reason : contract.terminate_reason) || "—"}</span>
            {contract.status === "TERMINATED" && contract.settled_amount !== null && (
              <>
                <span className="text-muted-foreground">기성고 정산액</span>
                <Money value={contract.settled_amount} />
              </>
            )}
          </div>
        </SectionCard>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_340px]">
        <div className="flex min-w-0 flex-col gap-y-4">
          <SectionCard padding="sm">
            <h2 className="mb-2 text-sm font-semibold">지급회차</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={HEAD}>회차</TableHead>
                  <TableHead className={`${HEAD} text-right`}>비율</TableHead>
                  <TableHead className={`${HEAD} text-right`}>금액</TableHead>
                  <TableHead className={HEAD}>지급 시기</TableHead>
                  {signed && <TableHead className={`${HEAD} ${PAID_HEAD}`}>받음</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className={`${CELL} whitespace-nowrap`}>{p.label}</TableCell>
                    <TableCell className={`${CELL} text-right tabular-nums`}>{bpToRate(p.ratio_bp, "percent")}%</TableCell>
                    <TableCell className={`${CELL} text-right tabular-nums whitespace-nowrap`}><Money value={p.amount} /></TableCell>
                    <TableCell className={CELL}>{p.trigger_text || "—"}</TableCell>
                    {signed && (
                      <TableCell className={PAID_CELL} data-testid="contract-payment-paid">
                        <span className="inline-flex items-center gap-x-1.5">
                          {p.paid_at && (
                            <span className="text-xs tabular-nums text-muted-foreground">{formatDate(p.paid_at)}</span>
                          )}
                          <PaymentPaidToggle
                            contractId={contract.id}
                            disabled={!paidEditable}
                            label={p.label}
                            paid={p.paid_at !== null}
                            paymentId={p.id}
                          />
                        </span>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>

          {signed && (
            <SectionCard padding="sm" data-testid="contract-change-orders">
              <div className="mb-2 flex items-center justify-between gap-x-2">
                <h2 className="text-sm font-semibold">변경공사</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <Button asChild size="sm" variant="ghost">
                    <Link href={statementPath(contract.id)}>
                      <FileText />
                      현재 내역서
                    </Link>
                  </Button>
                  {canChange && (
                    <Button asChild size="sm" variant="outline">
                      <Link href={newChangeOrderPath(contract.id)}>
                        <Plus />
                        변경공사 만들기
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
              <ChangeOrderList changeOrders={changeOrders} />
            </SectionCard>
          )}

          <SectionCard padding="sm">
            <h2 className="mb-2 text-sm font-semibold">특약</h2>
            {contract.special_terms.length === 0 ? (
              <p className="text-sm text-muted-foreground">특약이 없습니다.</p>
            ) : (
              <ol className="flex flex-col gap-y-2">
                {contract.special_terms.map((term) => (
                  <li key={term.no} className="flex flex-col gap-y-0.5 text-sm">
                    <span className="font-medium">
                      {term.no}. {term.title}
                    </span>
                    <span className="whitespace-pre-wrap text-muted-foreground">{term.body}</span>
                  </li>
                ))}
              </ol>
            )}
          </SectionCard>
        </div>

        <div className="flex flex-col gap-y-4">
          <SectionCard className="text-sm" padding="sm" data-testid="contract-amount">
            <h2 className="mb-2 text-sm font-semibold">계약금액</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">총 공사금액</dt>
              <dd className="text-right font-semibold tabular-nums">
                <Money value={amount.amount} /> ({totalVatNote(contractVatModeOf(amount))})
              </dd>
              <dt className="text-muted-foreground">공급가</dt>
              <dd className="text-right tabular-nums"><Money value={amount.supply_amount} /></dd>
              <dt className="text-muted-foreground">부가세</dt>
              <dd className="text-right tabular-nums"><Money value={amount.vat_amount} /></dd>
              {signed && (
                <>
                  <dt className="mt-1 border-t border-border pt-1 text-muted-foreground">원 계약금액</dt>
                  <dd className="mt-1 border-t border-border pt-1 text-right tabular-nums"><Money value={current.original} /></dd>
                  <dt className="text-muted-foreground">변경 합계</dt>
                  <dd className="text-right tabular-nums" data-testid="contract-change-total">
                    <Money value={current.accepted_delta} signed />
                  </dd>
                  <dt className="text-muted-foreground">현재 계약금액</dt>
                  <dd className="text-right font-semibold tabular-nums" data-testid="contract-current-total">
                    <Money value={current.current} />
                  </dd>
                </>
              )}
            </dl>
          </SectionCard>

          <SectionCard className="text-sm" padding="sm">
            <h2 className="mb-2 text-sm font-semibold">계약 조건</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">현장</dt>
              <dd className="text-right">{contract.site_address || "—"}</dd>
              <dt className="text-muted-foreground">착공일</dt>
              <dd className="text-right tabular-nums">{formatDate(contract.start_date)}</dd>
              <dt className="text-muted-foreground">준공일</dt>
              <dd className="text-right tabular-nums">{formatDate(contract.end_date)}</dd>
              {rates.map((f) => (
                <div key={f.key} className="contents">
                  <dt className="text-muted-foreground">{f.label}</dt>
                  <dd className="text-right tabular-nums">
                    {bpToRate(contract[f.key], f.unit)}
                    {RATE_UNIT_SUFFIX[f.unit]}
                  </dd>
                </div>
              ))}
              <dt className="text-muted-foreground">조항</dt>
              <dd className="text-right">{CLAUSE_SOURCE_LABEL[contract.clause_source]}</dd>
            </dl>
          </SectionCard>

          <SectionCard className="text-sm" padding="sm">
            <h2 className="mb-2 text-sm font-semibold">기록</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">만든 때</dt>
              <dd className="text-right tabular-nums">{formatDateTime(contract.created_at)}</dd>
              <dt className="text-muted-foreground">최근 수정</dt>
              <dd className="text-right tabular-nums">{formatDateTime(contract.updated_at)}</dd>
              <dt className="text-muted-foreground">발송</dt>
              <dd className="text-right tabular-nums">{formatDateTime(contract.sent_at)}</dd>
              <dt className="text-muted-foreground">체결</dt>
              <dd className="text-right tabular-nums">{formatDateTime(contract.signed_at)}</dd>
            </dl>
          </SectionCard>
        </div>
      </div>
    </>
  );
};

export default Page;
